/**
 * The Sales Tax switch (Tyler, 2026-10-21: "We need to be able to control
 * whether or not the sales tax and tax compliance page is turned on in the
 * admin app"; "when it is off we don't charge sales tax").
 *
 * An admin turns it on or off on Platform Fees, through the platform-config
 * endpoints (`platform.sales_tax_enabled`). Off, checkout charges no sales
 * tax, and web hides the Tax Compliance page and the Sales Tax on Rentals
 * guide with every way into them. Web reads it here, signed in or not: the
 * guide is a public page.
 *
 * WHY STRICT: the payload is read from the `app_config` table that also
 * holds the fee rates and the seller's permit (app.ts says the same of its
 * own payload). `.strict()` keeps a future "return the whole row" from
 * passing quietly to an unauthenticated caller.
 *
 * Mobile does not read it: it has no tax pages, and its checkout shows the
 * tax the server worked out.
 */
import { z } from 'zod';
import { envelope } from './_primitives';

export const SALES_TAX_ROUTES = {
  /** GET, public — whether the Sales Tax switch is on */
  switch: 'sales-tax/switch',
} as const;

/**
 * The switch's platform-config key: the row the backend reads, and the
 * `:key` admin's Platform Fees saves through `PUT admin/platform-config/:key`,
 * 'true' or 'false'.
 */
export const SALES_TAX_ENABLED_KEY = 'platform.sales_tax_enabled';

/** `GET /api/sales-tax/switch` payload. */
export const SalesTaxSwitchSchema = z
  .object({
    enabled: z.boolean(),
  })
  .strict();
export type SalesTaxSwitch = z.infer<typeof SalesTaxSwitchSchema>;

export const SalesTaxSwitchEnvelopeSchema = envelope(SalesTaxSwitchSchema);

export const unwrapSalesTaxSwitch = (
  r: z.infer<typeof SalesTaxSwitchEnvelopeSchema>,
): SalesTaxSwitch => r.data;
